import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Key,
  MapPin,
  Edit,
  ChevronRight,
  Eye,
  EyeOff,
  Users,
  CreditCard
} from 'lucide-react';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { 
  PSGCLocation, 
  getRegions,
  getProvinces,
  getProvincesByRegion,
  getCitiesMunicipalities,
  getCitiesByRegion,
  getBarangays 
} from '../utils/locationApi';

interface LocationFormData {
  region: PSGCLocation | null;
  province: PSGCLocation | null;
  city: PSGCLocation | null;
  barangay: PSGCLocation | null;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ProfilePageProps {
  isDeveloper: boolean;
  setIsDeveloper: (value: boolean) => void;
}

interface SubscriptionData {
  hasSubscription: boolean;
  subscription?: {
    id: string;
    status: string;
    currentPeriod: {
      start: string;
      end: string;
      daysRemaining: number;
    };
  };
}

const ProfilePage: React.FC<ProfilePageProps> = ({ isDeveloper, setIsDeveloper }) => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<'name' | 'email' | null>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  
  // Get name from user data - prefer preferredName, fallback to firstName + lastName, then email
  const displayName = user?.profile?.preferredName ||
                       (user?.profile?.firstName && user?.profile?.lastName
                         ? `${user.profile.firstName} ${user.profile.lastName}`
                         : user?.profile?.firstName || 'User');
  const displayEmail = user?.email || '';

  const [profileData, setProfileData] = useState({
    name: displayName,
    email: displayEmail,
    location: ''
  });

  // Password and location states
  const [passwordForm, setPasswordForm] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [locationForm, setLocationForm] = useState<LocationFormData>({
    region: null,
    province: null,
    city: null,
    barangay: null
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Location search states
  const [regions, setRegions] = useState<PSGCLocation[]>([]);
  const [provinces, setProvinces] = useState<PSGCLocation[]>([]);
  const [cities, setCities] = useState<PSGCLocation[]>([]);
  const [barangays, setBarangays] = useState<PSGCLocation[]>([]);
  const [searchTerms, setSearchTerms] = useState({
    region: '',
    province: '',
    city: '',
    barangay: ''
  });
  const [showDropdowns, setShowDropdowns] = useState({
    region: false,
    province: false,
    city: false,
    barangay: false
  });

  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);

  // Update profile data when user data changes
  useEffect(() => {
    if (user) {
      const newDisplayName = user?.profile?.preferredName ||
                             (user?.profile?.firstName && user?.profile?.lastName
                               ? `${user.profile.firstName} ${user.profile.lastName}`
                               : user?.profile?.firstName || 'User');
      const newDisplayEmail = user?.email || '';

      // Load location from user data
      let locationDisplay = '';
      if (user.location) {
        const locationParts = [];
        if (user.location.province?.name) locationParts.push(user.location.province.name);
        if (user.location.city?.name) locationParts.push(user.location.city.name);
        if (user.location.barangay?.name) locationParts.push(user.location.barangay.name);
        locationDisplay = locationParts.join(', ');
      }

      setProfileData({
        name: newDisplayName,
        email: newDisplayEmail,
        location: locationDisplay
      });
    }
  }, [user]);

  // Load regions on component mount
  useEffect(() => {
    loadRegions();
  }, []);

  // Fetch subscription data
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        const token = localStorage.getItem('token');
        const subscriptionResponse = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION}/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (subscriptionResponse.ok) {
          const data = await subscriptionResponse.json();
          console.log('Subscription data received:', data);
          
          // Check if data has the expected structure
          if (data.success && data.data) {
            setSubscriptionData(data.data);
          } else {

            setSubscriptionData({ hasSubscription: false });
          }
        } else if (subscriptionResponse.status === 404) {
          // No subscription found
          setSubscriptionData({ hasSubscription: false });
        } else {
          // Handle other error statuses
          const errorData = await subscriptionResponse.json().catch(() => ({}));

          setSubscriptionData({ hasSubscription: false });
        }
      } catch (error) {

        // Set default state on error
        setSubscriptionData({ hasSubscription: false });
      }
    };

    if (user) {
      fetchSubscriptionData();
    }

    // Listen for subscription refresh events
    const handleSubscriptionRefresh = () => {
      fetchSubscriptionData();
    };

    window.addEventListener('subscriptionRefresh', handleSubscriptionRefresh);

    return () => {
      window.removeEventListener('subscriptionRefresh', handleSubscriptionRefresh);
    };
  }, [user]);

  // Load provinces when region is selected
  useEffect(() => {
    if (locationForm.region) {
      loadProvinces(locationForm.region.code);
      setLocationForm(prev => ({ ...prev, province: null, city: null, barangay: null }));
    }
  }, [locationForm.region]);

  // Load cities for regions without provinces (like NCR)
  useEffect(() => {
    if (locationForm.region && provinces.length === 0) {
      loadCitiesForRegion(locationForm.region.code);
    }
  }, [provinces]);

  // Load cities when province is selected
  useEffect(() => {
    if (locationForm.province) {
      loadCities(locationForm.province.code);
      setLocationForm(prev => ({ ...prev, city: null, barangay: null }));
    }
  }, [locationForm.province]);

  // Load barangays when city is selected
  useEffect(() => {
    if (locationForm.city) {
      loadBarangays(locationForm.city.code);
      setLocationForm(prev => ({ ...prev, barangay: null }));
    }
  }, [locationForm.city]);

  const handleFieldClick = (field: 'name' | 'email') => {
    // Navigate to detailed profile page instead of opening modal
    navigate('/panel/profile/details');
  };

  // Location functions
  const loadRegions = async () => {
    try {
      const data = await getRegions();
      setRegions(data || []);
    } catch (error) {
      toast.error('Failed to load regions. Please try again.');
    }
  };

  const loadProvinces = async (regionCode: string) => {
    try {
      const data = await getProvincesByRegion(regionCode);
      setProvinces(data || []);
    } catch (error) {
      toast.error('Failed to load provinces. Please try again.');
    }
  };

  const loadCities = async (provinceCode: string) => {
    try {
      const data = await getCitiesMunicipalities(provinceCode);
      setCities(data || []);
    } catch (error) {
      toast.error('Failed to load cities/municipalities. Please try again.');
    }
  };

  const loadCitiesForRegion = async (regionCode: string) => {
    try {
      const data = await getCitiesByRegion(regionCode);
      setCities(data || []);
    } catch (error) {
      toast.error('Failed to load cities/municipalities. Please try again.');
    }
  };

  const loadBarangays = async (cityCode: string) => {
    try {
      const data = await getBarangays(cityCode);
      setBarangays(data || []);
    } catch (error) {
      toast.error('Failed to load barangays. Please try again.');
    }
  };

  // Password change functions
  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
          confirmPassword: passwordForm.confirmPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      toast.success('Password changed successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsPasswordModalOpen(false);
    } catch (error) {

      toast.error(error instanceof Error ? error.message : 'Failed to change password. Please try again.');
    }
  };

  // Location save function
  const handleLocationSave = async () => {
    // Region is always required
    if (!locationForm.region) {
      toast.error('Please select a region');
      return;
    }

    // Province is only required if there are provinces for this region
    if (provinces.length > 0 && !locationForm.province) {
      toast.error('Please select a province');
      return;
    }

    // City and Barangay are always required
    if (!locationForm.city || !locationForm.barangay) {
      toast.error('Please select city/municipality and barangay');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Build location data in proper format for backend
      const locationData: any = {
        region: locationForm.region ? {
          code: locationForm.region.code,
          name: locationForm.region.name
        } : undefined,
        province: locationForm.province ? {
          code: locationForm.province.code,
          name: locationForm.province.name
        } : undefined,
        city: {
          code: locationForm.city.code,
          name: locationForm.city.name
        },
        barangay: {
          code: locationForm.barangay.code,
          name: locationForm.barangay.name
        }
      };

      // Remove undefined fields if they weren't selected
      if (!locationData.region) {
        delete locationData.region;
      }
      if (!locationData.province) {
        delete locationData.province;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/location`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          location: locationData,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update location');
      }

      // Create location display string
      const locationParts = [];
      if (locationForm.province?.name) locationParts.push(locationForm.province.name);
      if (locationForm.city?.name) locationParts.push(locationForm.city.name);
      if (locationForm.barangay?.name) locationParts.push(locationForm.barangay.name);
      const locationDisplay = locationParts.join(', ');

      // Update profile data with location
      setProfileData(prev => ({
        ...prev,
        location: locationDisplay
      }));

      // Update user in AuthContext including location
      if (user && data.success) {
        // Use the user data from response if available, otherwise construct it
        const updatedUserData = data.data?.user || {
          ...user,
          location: data.data?.location || {
            province: locationForm.province ? {
              code: locationForm.province.code,
              name: locationForm.province.name
            } : undefined,
            city: {
              code: locationForm.city.code,
              name: locationForm.city.name
            },
            barangay: {
              code: locationForm.barangay.code,
              name: locationForm.barangay.name
            }
          }
        };
        
        const updatedUser: typeof user = {
          id: updatedUserData.id || user.id,
          email: updatedUserData.email || user.email,
          profile: updatedUserData.profile || user.profile,
          location: updatedUserData.location,
          isEmailVerified: updatedUserData.isEmailVerified ?? user.isEmailVerified,
          role: updatedUserData.role || user.role,
          lastLogin: updatedUserData.lastLogin || user.lastLogin,
          lastActive: updatedUserData.lastActive || user.lastActive,
          createdAt: updatedUserData.createdAt || user.createdAt
        };
        updateUser(updatedUser);
      }

      toast.success('Location updated successfully!');
      setIsLocationModalOpen(false);
    } catch (error) {

      toast.error(error instanceof Error ? error.message : 'Failed to update location. Please try again.');
    }
  };

  const handleSave = async (newValue: string) => {
    if (!editingField || !user) return;

    // Only allow name updates (email updates require mobile app)
    if (editingField !== 'name') {
      toast.warning('Email updates must be done through the LIFE PSENsOR mobile app.');
      setIsModalOpen(false);
      setEditingField(null);
      return;
    }

    try {
      // Prepare the update payload for preferred name
      const updatePayload = { 
        profile: { preferredName: newValue } 
      };

      // Make API call to update profile
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(updatePayload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      // Update local state
      setProfileData(prev => ({
        ...prev,
        name: newValue
      }));

      // Update auth context with new user data
      if (data.success && data.data.user) {
        const updatedUser = {
          id: data.data.user.id,
          email: data.data.user.email,
          profile: data.data.user.profile,
          isEmailVerified: data.data.user.isEmailVerified,
          role: data.data.user.role,
          lastLogin: data.data.user.lastLogin,
          lastActive: data.data.user.lastActive,
          createdAt: data.data.user.createdAt
        };
        
        // Update user in AuthContext (this also updates localStorage)
        updateUser(updatedUser);
      }

      setIsModalOpen(false);
      setEditingField(null);
    } catch (error) {

      toast.error(error instanceof Error ? error.message : 'Failed to update profile. Please try again.');
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-page__header">
        <h1 className="profile-page__title">Profile</h1>
      </div>

      <div className="profile-page__content">
        {/* Profile Section */}
        <div className="profile-card">
          <div className="profile-card__header">
            <h3 className="profile-card__title">Profile</h3>
          </div>
          <div className="profile-card__body">
            <div className="profile-user">
              <div className="profile-user__avatar">
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(profileData.name)}&background=4F46E5&color=ffffff&size=96`}
                  alt="User Avatar"
                  className="profile-user__avatar-img"
                />
              </div>
              <div className="profile-user__info">
                <div className="profile-user__details">
                  <h4 className="profile-user__name">{profileData.name}</h4>
                  <p className="profile-user__email">{profileData.email}</p>
                </div>
                <button
                  className="profile-user__edit-btn"
                  onClick={() => handleFieldClick('name')}
                >
                  <Edit className="profile-user__edit-icon" />
                  <span>Edit</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Email and Subscription Section - Only show for admins */}
        {user?.role && user.role.toLowerCase() === 'admin' && (
          <div className="profile-card profile-card--subscription">
            <div className="profile-card__header">
              <h3 className="profile-card__title">Subscription</h3>
            </div>
            <div className="profile-card__body">
                <button
                  className="subscription-nav-item subscription-nav-item--inline"
                  onClick={() => navigate('/panel/subscription')}
                >
                  <div className="profile-nav-item__content">
                    <div className="profile-nav-item__icon">
                      <CreditCard className="profile-nav-item__icon-svg" />
                    </div>
                    <div className="profile-nav-item__text">
                      <span className="profile-nav-item__title">Subscription</span>
                      <span className="profile-nav-item__description">Manage your subscription plan</span>
                    </div>
                  </div>
                  <ChevronRight className="profile-nav-item__arrow" />
                </button>
                {/* Subscription Status */}
                <div className="profile-subscription-status">
                  {subscriptionData?.hasSubscription && subscriptionData.subscription ? (
                    <>
                      <span className={`profile-subscription-status__badge profile-subscription-status__badge--${subscriptionData.subscription.status}`}>
                        {subscriptionData.subscription.status === 'active' ? 'Paid' :
                         subscriptionData.subscription.status === 'pending' ? 'Pending' :
                         subscriptionData.subscription.status.replace('_', ' ').toUpperCase()}
                      </span>
                      {subscriptionData.subscription.status === 'active' && subscriptionData.subscription.currentPeriod.daysRemaining !== undefined && (
                        <span className="profile-subscription-status__days">
                          {subscriptionData.subscription.currentPeriod.daysRemaining} {subscriptionData.subscription.currentPeriod.daysRemaining === 1 ? 'day' : 'days'} remaining
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="profile-subscription-status__badge profile-subscription-status__badge--none">
                      No Subscription
                    </span>
                  )}
                </div>
            </div>
          </div>
        )}
        {/* Change Password Section */}
        <div className="profile-card">
          <div className="profile-card__header">
            <h3 className="profile-card__title">Security</h3>
          </div>
          <div className="profile-card__body">
            <button
              className="profile-nav-item"
              onClick={() => setIsPasswordModalOpen(true)}
            >
              <div className="profile-nav-item__content">
                <div className="profile-nav-item__icon">
                  <Key className="profile-nav-item__icon-svg" />
                </div>
                <div className="profile-nav-item__text">
                  <span className="profile-nav-item__title">Change Password</span>
                  <span className="profile-nav-item__description">Update your account password</span>
                </div>
              </div>
              <ChevronRight className="profile-nav-item__arrow" />
            </button>
            <Link
              to="/panel/data-permissions"
              className="profile-nav-item"
            >
              <div className="profile-nav-item__content">
                <div className="profile-nav-item__icon">
                  <Users className="profile-nav-item__icon-svg" />
                </div>
                <div className="profile-nav-item__text">
                  <span className="profile-nav-item__title">Coach Permissions</span>
                  <span className="profile-nav-item__description">Manage coach access and permissions</span>
                </div>
              </div>
              <ChevronRight className="profile-nav-item__arrow" />
            </Link>
          </div>
        </div>

        {/* Location Section */}
        <div className="profile-card">
          <div className="profile-card__header">
            <h3 className="profile-card__title">Location</h3>
          </div>
          <div className="profile-card__body">
            <button
              className="profile-nav-item"
              onClick={() => setIsLocationModalOpen(true)}
            >
              <div className="profile-nav-item__content">
                <div className="profile-nav-item__icon">
                  <MapPin className="profile-nav-item__icon-svg" />
                </div>
                <div className="profile-nav-item__text">
                  <span className="profile-nav-item__title">Update Location</span>
                  <span className="profile-nav-item__description">
                    {profileData.location || 'Set your province, city, and barangay'}
                  </span>
                </div>
              </div>
              <ChevronRight className="profile-nav-item__arrow" />
            </button>
          </div>
        </div>

      </div>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content edit-field-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal__close" onClick={() => setIsModalOpen(false)}><span className="material-symbols-outlined">close</span></button>
        <EditFieldModal
          field={editingField}
          currentValue={editingField ? profileData[editingField] : ''}
          onSave={handleSave}
          onCancel={() => setIsModalOpen(false)}
        />
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {isPasswordModalOpen && (
        <div className="modal-overlay" onClick={() => setIsPasswordModalOpen(false)}>
          <div className="modal-content password-change-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal__close" onClick={() => setIsPasswordModalOpen(false)}><span className="material-symbols-outlined">close</span></button>
        <PasswordChangeModal
          passwordForm={passwordForm}
          setPasswordForm={setPasswordForm}
          showPasswords={showPasswords}
          setShowPasswords={setShowPasswords}
          onSave={handlePasswordChange}
          onCancel={() => setIsPasswordModalOpen(false)}
        />
          </div>
        </div>
      )}

      {/* Location Modal */}
      {isLocationModalOpen && (
        <div className="modal-overlay" onClick={() => setIsLocationModalOpen(false)}>
          <div className="modal-content location-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal__close" onClick={() => setIsLocationModalOpen(false)}><span className="material-symbols-outlined">close</span></button>
        <LocationModal
          locationForm={locationForm}
          setLocationForm={setLocationForm}
          regions={regions}
          provinces={provinces}
          cities={cities}
          barangays={barangays}
          searchTerms={searchTerms}
          setSearchTerms={setSearchTerms}
          showDropdowns={showDropdowns}
          setShowDropdowns={setShowDropdowns}
          onSave={handleLocationSave}
          onCancel={() => setIsLocationModalOpen(false)}
        />
          </div>
        </div>
      )}

    </div>
  );
};

interface EditFieldModalProps {
  field: 'name' | 'email' | null;
  currentValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}

const EditFieldModal: React.FC<EditFieldModalProps> = ({ field, currentValue, onSave, onCancel }) => {
  const [value, setValue] = useState(currentValue);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(value);
  };

  if (!field) return null;

  return (
    <div className="edit-field-modal">
      <h2>Edit {field === 'name' ? 'Preferred Name' : 'Email'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="field-group">
          <label>{field === 'name' ? 'Preferred Name' : 'Email Address'}</label>
        <input
          type={field === 'email' ? 'email' : 'text'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={`Enter your ${field === 'name' ? 'preferred name' : 'email address'}`}
          className="edit-input"
          autoFocus
        />
        </div>
        <div className="modal-buttons">
          <Button
            variant="secondary"
            className="btn-cancel"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="btn-save"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};

interface PasswordChangeModalProps {
  passwordForm: PasswordFormData;
  setPasswordForm: (form: PasswordFormData) => void;
  showPasswords: { current: boolean; new: boolean; confirm: boolean };
  setShowPasswords: (show: { current: boolean; new: boolean; confirm: boolean }) => void;
  onSave: () => void;
  onCancel: () => void;
}

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({
  passwordForm,
  setPasswordForm,
  showPasswords,
  setShowPasswords,
  onSave,
  onCancel
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave();
  };

  return (
    <div className="password-change-modal">
      <h2>Change Password</h2>
      <form onSubmit={handleSubmit}>
        <div className="password-field">
          <label>Current Password</label>
          <div className="password-input-container">
            <input
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              placeholder="Enter current password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
              className="password-toggle"
            >
              {showPasswords.current ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="password-field">
          <label>New Password</label>
          <div className="password-input-container">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              placeholder="Enter new password"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
              className="password-toggle"
            >
              {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="password-field">
          <label>Confirm New Password</label>
          <div className="password-input-container">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              placeholder="Confirm new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
              className="password-toggle"
            >
              {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="modal-buttons">
          <button type="button" onClick={onCancel} className="btn-cancel">
            Cancel
          </button>
          <button type="submit" className="btn-save">
            Change Password
          </button>
        </div>
      </form>
    </div>
  );
};

interface LocationModalProps {
  locationForm: LocationFormData;
  setLocationForm: (form: LocationFormData) => void;
  regions: PSGCLocation[];
  provinces: PSGCLocation[];
  cities: PSGCLocation[];
  barangays: PSGCLocation[];
  searchTerms: { region: string; province: string; city: string; barangay: string };
  setSearchTerms: (terms: { region: string; province: string; city: string; barangay: string }) => void;
  showDropdowns: { region: boolean; province: boolean; city: boolean; barangay: boolean };
  setShowDropdowns: (show: { region: boolean; province: boolean; city: boolean; barangay: boolean }) => void;
  onSave: () => void;
  onCancel: () => void;
}

const LocationModal: React.FC<LocationModalProps> = ({
  locationForm,
  setLocationForm,
  regions,
  provinces,
  cities,
  barangays,
  searchTerms,
  setSearchTerms,
  showDropdowns,
  setShowDropdowns,
  onSave,
  onCancel
}) => {
  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const dropdownContainer = target.closest('.dropdown-container');
      
      if (!dropdownContainer) {
        setShowDropdowns({ region: false, province: false, city: false, barangay: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowDropdowns]);

  const filteredRegions = regions.filter(r =>
    r.name.toLowerCase().includes(searchTerms.region.toLowerCase())
  );

  const filteredProvinces = provinces.filter(p =>
    p.name.toLowerCase().includes(searchTerms.province.toLowerCase())
  );

  const filteredCities = cities.filter(c =>
    c.name.toLowerCase().includes(searchTerms.city.toLowerCase())
  );

  const filteredBarangays = barangays.filter(b =>
    b.name.toLowerCase().includes(searchTerms.barangay.toLowerCase())
  );

  return (
    <div className="location-modal">
      <div className="location-modal__content">
        <h2>Update Location</h2>
        <p className="location-modal__description">
          Select your {provinces.length > 0 ? 'region, province, city/municipality, and barangay' : 'region, city/municipality, and barangay'}
        </p>

        {/* Progress Indicator */}
      <div className="location-progress">
        <div className={`progress-step ${locationForm.region ? 'completed' : ''} ${searchTerms.region ? 'active' : ''}`}></div>
        {provinces.length > 0 && <div className={`progress-step ${locationForm.province ? 'completed' : ''} ${searchTerms.province ? 'active' : ''}`}></div>}
        <div className={`progress-step ${locationForm.city ? 'completed' : ''} ${searchTerms.city ? 'active' : ''}`}></div>
        <div className={`progress-step ${locationForm.barangay ? 'completed' : ''} ${searchTerms.barangay ? 'active' : ''}`}></div>
      </div>

      {/* Region Selection */}
      <div className="location-field">
        <label>
          Region <span className="required">*</span>
          {locationForm.region && <span className="field-status">✓ Selected</span>}
        </label>
        <div className="dropdown-container">
          <input
            type="text"
            value={searchTerms.region}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchTerms({ ...searchTerms, region: newValue });
              // Clear selection if user modifies the input
              if (newValue === '' && locationForm.region) {
                setLocationForm({ ...locationForm, region: null, province: null, city: null, barangay: null });
              }
              setShowDropdowns({ ...showDropdowns, region: true });
            }}
            onFocus={() => setShowDropdowns({ ...showDropdowns, region: true })}
            placeholder="Search region..."
            className="location-input"
          />
          {locationForm.region && (
            <button 
              onClick={() => {
                setLocationForm({...locationForm, region: null, province: null, city: null, barangay: null});
                setSearchTerms({...searchTerms, region: '', province: '', city: '', barangay: ''});
                setShowDropdowns({...showDropdowns, region: false, province: false, city: false, barangay: false});
              }} 
              className="field-clear-btn"
              type="button"
              aria-label="Clear region"
            >
              ×
            </button>
          )}
          {showDropdowns.region && (
            <div className="dropdown-list">
              {filteredRegions.length > 0 ? (
                filteredRegions.map((region) => (
                  <div
                    key={region.code}
                    className="dropdown-item"
                    onClick={() => {
                      const displayName = region.regionName && region.name && region.regionName !== region.name 
                        ? `${region.regionName} (${region.name})`
                        : region.regionName || region.name;
                      setLocationForm({ ...locationForm, region, province: null, city: null, barangay: null });
                      setSearchTerms({ ...searchTerms, region: displayName, province: '', city: '', barangay: '' });
                      setShowDropdowns({ region: false, province: false, city: false, barangay: false });
                    }}
                  >
                    {region.regionName && region.name && region.regionName !== region.name 
                      ? `${region.regionName} (${region.name})`
                      : region.regionName || region.name}
                  </div>
                ))
              ) : (
                <div className="dropdown-empty">
                  {searchTerms.region ? 'No regions found' : 'Type to search regions'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Province Selection - Only show if region has provinces */}
      {provinces.length > 0 && (
      <div className="location-field">
        <label>
          Province <span className="required">*</span>
          {locationForm.province && <span className="field-status">✓ Selected</span>}
        </label>
        <div className="dropdown-container">
          <input
            type="text"
            value={searchTerms.province}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchTerms({ ...searchTerms, province: newValue });
              // Clear selection if user modifies the input
              if (newValue === '' && locationForm.province) {
                setLocationForm({ ...locationForm, province: null, city: null, barangay: null });
              }
              setShowDropdowns({ ...showDropdowns, province: true });
            }}
            onFocus={() => setShowDropdowns({ ...showDropdowns, province: true })}
            placeholder={locationForm.region ? 'Search province...' : 'Select a region first'}
            className="location-input"
            disabled={!locationForm.region}
          />
          {locationForm.province && (
            <button 
              onClick={() => {
                setLocationForm({...locationForm, province: null, city: null, barangay: null});
                setSearchTerms({...searchTerms, province: '', city: '', barangay: ''});
                setShowDropdowns({...showDropdowns, province: false, city: false, barangay: false});
              }} 
              className="field-clear-btn"
              type="button"
              aria-label="Clear province"
            >
              ×
            </button>
          )}
          {showDropdowns.province && locationForm.region && (
            <div className="dropdown-list">
              {filteredProvinces.length > 0 ? (
                filteredProvinces.map((province) => (
                  <div
                    key={province.code}
                    className="dropdown-item"
                    onClick={() => {
                      setLocationForm({ ...locationForm, province, city: null, barangay: null });
                      setSearchTerms({ ...searchTerms, province: province.name, city: '', barangay: '' });
                      setShowDropdowns({ region: false, province: false, city: false, barangay: false });
                    }}
                  >
                    {province.name}
                  </div>
                ))
              ) : (
                <div className="dropdown-empty">
                  {searchTerms.province ? 'No provinces found' : `${provinces.length} provinces available`}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* City Selection */}
      <div className="location-field">
        <label>
          City/Municipality <span className="required">*</span>
          {locationForm.city && <span className="field-status">✓ Selected</span>}
        </label>
        <div className="dropdown-container">
          <input
            type="text"
            value={searchTerms.city}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchTerms({ ...searchTerms, city: newValue });
              // Clear selection if user modifies the input
              if (newValue === '' && locationForm.city) {
                setLocationForm({ ...locationForm, city: null, barangay: null });
              }
              setShowDropdowns({ ...showDropdowns, city: true });
            }}
            onFocus={() => setShowDropdowns({ ...showDropdowns, city: true })}
            placeholder={
              provinces.length > 0
                ? locationForm.province ? 'Search city/municipality...' : 'Select a province first'
                : locationForm.region ? 'Search city/municipality...' : 'Select a region first'
            }
            className="location-input"
            disabled={provinces.length > 0 ? !locationForm.province : !locationForm.region}
          />
          {locationForm.city && (
            <button 
              onClick={() => {
                setLocationForm({...locationForm, city: null, barangay: null});
                setSearchTerms({...searchTerms, city: '', barangay: ''});
                setShowDropdowns({...showDropdowns, city: false, barangay: false});
              }} 
              className="field-clear-btn"
              type="button"
              aria-label="Clear city/municipality"
            >
              ×
            </button>
          )}
          {showDropdowns.city && (provinces.length > 0 ? locationForm.province : locationForm.region) && (
            <div className="dropdown-list">
              {filteredCities.length > 0 ? (
                filteredCities.map((city) => (
                  <div
                    key={city.code}
                    className="dropdown-item"
                    onClick={() => {
                      setLocationForm({ ...locationForm, city, barangay: null });
                      setSearchTerms({ ...searchTerms, city: city.name, barangay: '' });
                      setShowDropdowns({ ...showDropdowns, city: false, barangay: false });
                    }}
                  >
                    {city.name}
                  </div>
                ))
              ) : (
                <div className="dropdown-empty">
                  {searchTerms.city ? 'No cities/municipalities found' : `${cities.length} cities/municipalities available`}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Barangay Selection */}
      <div className="location-field location-field--barangay">
        <label>
          Barangay <span className="required">*</span>
          {locationForm.barangay && <span className="field-status">✓ Selected</span>}
        </label>
        <div className="dropdown-container">
          <input
            type="text"
            value={searchTerms.barangay}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchTerms({ ...searchTerms, barangay: newValue });
              // Clear selection if user modifies the input
              if (newValue === '' && locationForm.barangay) {
                setLocationForm({ ...locationForm, barangay: null });
              }
              setShowDropdowns({ ...showDropdowns, barangay: true });
            }}
            onFocus={() => setShowDropdowns({ ...showDropdowns, barangay: true })}
            placeholder={locationForm.city ? 'Search barangay...' : 'Select a city/municipality first'}
            className="location-input"
            disabled={!locationForm.city}
          />
          {locationForm.barangay && (
            <button 
              onClick={() => {
                setLocationForm({...locationForm, barangay: null});
                setSearchTerms({...searchTerms, barangay: ''});
                setShowDropdowns({...showDropdowns, barangay: false});
              }} 
              className="field-clear-btn"
              type="button"
              aria-label="Clear barangay"
            >
              ×
            </button>
          )}
          {showDropdowns.barangay && locationForm.city && (
            <div className="dropdown-list">
              {filteredBarangays.length > 0 ? (
                filteredBarangays.map((barangay) => (
                  <div
                    key={barangay.code}
                    className="dropdown-item"
                    onClick={() => {
                      setLocationForm({ ...locationForm, barangay });
                      setSearchTerms({ ...searchTerms, barangay: barangay.name });
                      setShowDropdowns({ ...showDropdowns, barangay: false });
                    }}
                  >
                    {barangay.name}
                  </div>
                ))
              ) : (
                <div className="dropdown-empty">
                  {searchTerms.barangay ? 'No barangays found' : `${barangays.length} barangays available`}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      <div className="modal-buttons">
        <button type="button" onClick={onCancel} className="btn-cancel">
          Cancel
        </button>
        <button type="button" onClick={onSave} className="btn-save">
          Update Location
        </button>
      </div>
    </div>
  );
};


export default ProfilePage;


